import { buildSettingsBadge } from '@/lib/chat/settings-badge'
import type { CatalogCategory, CatalogModel, ModelStatus } from '@/types/catalog'
import type { ChatMessage, ChatSession, SendMessageResult, SessionSettings } from '@/types/chat'
import type { CompareBatch, CompareRating, RatingDto } from '@/types/compare'

type JsonRecord = Record<string, unknown>

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === 'object' ? (value as JsonRecord) : {}
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' ? value : fallback
}

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asOptionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined
}

export function mapSessionSettings(value: unknown): SessionSettings {
  const record = asRecord(value)
  return {
    temperature: asOptionalNumber(record.temperature),
    topP: asOptionalNumber(record.top_p),
    maxTokens: asOptionalNumber(record.max_tokens),
    frequencyPenalty: asOptionalNumber(record.frequency_penalty),
    presencePenalty: asOptionalNumber(record.presence_penalty),
    seed: asOptionalNumber(record.seed),
    responseFormat: asString(record.response_format) || undefined,
    jsonMode: asBool(record.json_mode),
  }
}

export function mapCatalogCategory(item: unknown): CatalogCategory {
  const record = asRecord(item)
  return {
    id: asString(record.id),
    name: asString(record.name),
    slug: asString(record.slug),
  }
}

export function mapCatalogModel(item: unknown): CatalogModel {
  const record = asRecord(item)
  const categoryStatus = asRecord(record.category_status)
  const pricing = asRecord(record.pricing)
  const features = asRecord(record.features)
  const rawStatus = asString(categoryStatus.status, 'active')
  const status: ModelStatus =
    rawStatus === 'active' ||
    rawStatus === 'archived' ||
    rawStatus === 'hidden' ||
    rawStatus === 'blacklisted'
      ? rawStatus
      : 'active'

  return {
    id: asString(record.id),
    categoryId: asString(categoryStatus.category_id),
    providerId: asString(record.provider_id),
    providerName: asString(record.provider_name),
    displayName: asString(record.display_name),
    slug: asString(record.slug),
    description: asString(record.description),
    contextWindow: asNumber(record.context_window, 0),
    pricing: {
      inputPerMillion: asNumber(pricing.input_per_million_tokens),
      outputPerMillion: asNumber(pricing.output_per_million_tokens),
      isFree: asBool(pricing.is_free),
    },
    status,
    isPinned: asBool(categoryStatus.is_pinned),
    categoryScore: asNumber(categoryStatus.category_score),
    userScore: asNumber(categoryStatus.user_score),
    features: {
      toolCalling: asBool(features.tool_calling),
      vision: asBool(features.vision),
      streaming: asBool(features.streaming, true),
    },
  }
}

export function mapSessionListItem(item: unknown): ChatSession {
  const record = asRecord(item)
  return {
    id: asString(record.id),
    modelId: asString(record.model_id),
    modelName: asString(record.model_name),
    title: asString(record.title),
    status: asString(record.status, 'open') as ChatSession['status'],
    defaultSettings: {},
    messages: [],
    createdAt: asString(record.created_at),
    lastMessageAt: asString(record.last_message_at) || undefined,
    closedAt: asString(record.closed_at) || undefined,
  }
}

export function mapSessionDetails(item: unknown): ChatSession {
  const record = asRecord(item)
  const model = asRecord(record.model)
  const messages = Array.isArray(record.messages) ? record.messages : []

  return {
    id: asString(record.id),
    modelId: asString(record.model_id),
    modelName: asString(model.display_name),
    title: asString(record.title),
    status: asString(record.status, 'open') as ChatSession['status'],
    systemPrompt: asString(record.system_prompt) || undefined,
    defaultSettings: mapSessionSettings(record.default_settings),
    messages: messages.map(mapChatMessage),
    createdAt: asString(record.created_at),
    lastMessageAt: asString(record.last_message_at) || undefined,
    closedAt: asString(record.closed_at) || undefined,
  }
}

export function mapChatMessage(item: unknown): ChatMessage {
  const record = asRecord(item)
  const settingsSnapshot = mapSessionSettings(record.settings_snapshot)
  const responseRecord = record.response ? asRecord(record.response) : null

  return {
    id: asString(record.id),
    promptRequestId: asString(record.prompt_request_id),
    promptText: asString(record.prompt_text),
    settingsSnapshot,
    settingsBadge: asString(record.settings_badge) || buildSettingsBadge(settingsSnapshot),
    response: responseRecord
      ? {
          id: asString(responseRecord.id),
          normalizedText: asString(responseRecord.normalized_text),
          tokensInput: asNumber(responseRecord.tokens_input),
          tokensOutput: asNumber(responseRecord.tokens_output),
          estimatedCost: asNumber(responseRecord.estimated_cost),
          latencyMs: asNumber(responseRecord.latency_ms),
          status: asString(responseRecord.status),
        }
      : undefined,
    createdAt: asString(record.created_at),
  }
}

export function mapSendMessageResult(item: unknown): SendMessageResult {
  const record = asRecord(item)
  return {
    requestId: asString(record.request_id),
    responseId: asString(record.response_id),
    normalizedText: asString(record.normalized_text),
    tokensInput: asNumber(record.tokens_input),
    tokensOutput: asNumber(record.tokens_output),
    estimatedCost: asNumber(record.estimated_cost),
    latencyMs: asNumber(record.latency_ms),
    status: asString(record.status),
  }
}

export function mapCompareBatch(item: unknown): CompareBatch {
  const record = asRecord(item)
  const responses = Array.isArray(record.responses) ? record.responses : []

  return {
    id: asString(record.id),
    title: asString(record.title) || undefined,
    promptText: asString(record.prompt_text),
    createdAt: asString(record.created_at),
    responses: responses.map(mapCompareResponse),
  }
}

function mapCompareResponse(item: unknown): CompareBatch['responses'][number] {
  const record = asRecord(item)
  const model = asRecord(record.model)
  const provider = asRecord(record.provider)
  const category = record.category ? asRecord(record.category) : null
  const settingsSnapshot = mapSessionSettings(record.settings_snapshot)

  return {
    modelResponseId: asString(record.model_response_id),
    sessionId: asString(record.session_id),
    model: {
      displayName: asString(model.display_name),
      slug: asString(model.slug),
    },
    provider: { name: asString(provider.name) },
    category: category ? { name: asString(category.name) } : undefined,
    settingsSnapshot,
    settingsBadge: buildSettingsBadge(settingsSnapshot),
    normalizedText: asString(record.normalized_text) || undefined,
    tokensInput: asOptionalNumber(record.tokens_input),
    tokensOutput: asOptionalNumber(record.tokens_output),
    estimatedCost: asOptionalNumber(record.estimated_cost),
    latencyMs: asOptionalNumber(record.latency_ms),
    status: asString(record.status),
    errorMessage: asString(record.error_message) || undefined,
    rating: record.rating ? mapCompareRating(record.rating) : null,
  }
}

function mapCompareRating(item: unknown): CompareRating {
  const record = asRecord(item)
  const thumbs = asString(record.thumbs)
  return {
    id: asString(record.id),
    modelResponseId: asString(record.model_response_id),
    ratingStars: asOptionalNumber(record.rating_stars),
    thumbs: thumbs === 'up' || thumbs === 'down' ? thumbs : undefined,
    isBestAnswer: asBool(record.is_best_answer),
    comment: asString(record.comment) || undefined,
    createdAt: asString(record.created_at),
  }
}

export function mapRatingDto(item: unknown): RatingDto {
  const record = asRecord(item)
  const thumbs = asString(record.thumbs)
  return {
    id: asString(record.id),
    modelResponseId: asString(record.model_response_id),
    ratingStars: asOptionalNumber(record.rating_stars),
    thumbs: thumbs === 'up' || thumbs === 'down' ? thumbs : undefined,
    isBestAnswer: asBool(record.is_best_answer),
    comment: asString(record.comment) || undefined,
    createdAt: asString(record.created_at),
  }
}

export function toSessionSettingsPayload(settings: SessionSettings): JsonRecord {
  return {
    temperature: settings.temperature,
    top_p: settings.topP,
    max_tokens: settings.maxTokens,
    frequency_penalty: settings.frequencyPenalty,
    presence_penalty: settings.presencePenalty,
    seed: settings.seed,
    response_format: settings.responseFormat,
    json_mode: settings.jsonMode,
  }
}
